import React, { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { postData, fetchData } from '../../../apiService';
import Select from 'react-select';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import CircularProgress from '@mui/material/CircularProgress';
import ReceiptIcon from '@mui/icons-material/Receipt';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const inpR = { ...inp, textAlign: 'right' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const selectStyles = {
  control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '12px', borderRadius: '7px', borderColor: C.cardBorder }),
  valueContainer: (base) => ({ ...base, height: '28px', padding: '0 6px' }),
  indicatorsContainer: (base) => ({ ...base, height: '28px' }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
  menu: (base) => ({ ...base, fontSize: '12px' }),
};

const ReceiptCreate = () => {
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage]     = useState('');
  const [invoiceDetails, setInvoiceDetails] = useState([]);

  const [customerOptions, setCustomerOptions]           = useState([]);
  const [selectedCustomer, setSelectedCustomer]         = useState(null);
  const [paymentmethodOptions, setPaymentmethodOptions] = useState([]);
  const [selectedPaymentmethodType, setSelectedPaymentmethodType] = useState(null);
  const [bankOptions, setBankOptions]   = useState([]);
  const [selectedBank, setSelectedBank] = useState(null);
  const [showBankField, setShowBankField] = useState(false);

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [formData, setFormData] = useState({
    CompanyId: companyId, BranchId: branchId,
    BillDate: '', BillNo: 0, FinYear: '',
    MasterId: '', CreatedBy: userId,
    Amount: '', Remarks: '',
    PaymentType: 1, ChequeNumber: '', ChequeDate: '', chequeDetails: '',
    TotalBalAmt: 0,
  });

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchmaxbillno();
    fetchPaymentmethodTypes();
    fetchBanks();
    fetchCustomerData();
    const today = formatDateForInput(new Date());
    setFormData(prev => ({ ...prev, BillDate: today, ChequeDate: today }));
  }, []);

  const fetchmaxbillno = () => {
    fetchData('ReceiptApi', `/MaxBillno/${branchId}/${companyId}`, token)
      .then(res => res.response_data)
      .then(data => {
        const today = formatDateForInput(new Date());
        setFormData(prev => ({ ...prev, BillNo: data.BillNumber, BillDate: today, FinYear: data.FinYear, BranchId: branchId, CompanyId: companyId }));
      })
      .catch(err => console.error('Error fetching max bill no:', err));
  };

  const fetchPaymentmethodTypes = async () => {
    try {
      const res = await fetchData('ReceiptApi', `/PaymentModes/${companyId}`, token);
      if (res.responseCode === '1111') setPaymentmethodOptions(res.response_data.map(m => ({ value: m.Id, label: m.Name })));
    } catch (err) { console.error('Error fetching payment modes:', err); }
  };

  const fetchBanks = async () => {
    try {
      const res = await fetchData('ReceiptApi', `/Banks/${companyId}`, token);
      if (res.responseCode === '1111') setBankOptions(res.response_data.map(m => ({ value: m.Id, label: m.Name })));
    } catch (err) { console.error('Error fetching banks:', err); }
  };

  const fetchCustomerData = () => {
    fetchData('ReceiptApi', `/Party/${companyId}`, token)
      .then(res => res.response_data)
      .then(data => setCustomerOptions(data.map(c => ({ label: c.Name, value: c.Id }))))
      .catch(err => console.error('Error fetching customers:', err));
  };

  const handleCustomerSelect = (selected) => {
    setSelectedCustomer(selected);
    setFormData(prev => ({ ...prev, MasterId: selected?.value || '', CreatedBy: userId }));
    if (selected) {
      fetchData('ReceiptApi', `/PartyReceiptBill/${selected.value}/${companyId}`, token)
        .then(res => {
          if (res.responseCode === '1111') {
            const details = res.response_data;
            const total = details.reduce((sum, d) => sum + d.PendingAmount, 0);
            setFormData(prev => ({ ...prev, TotalBalAmt: total }));
            setInvoiceDetails(details);
          }
        })
        .catch(err => console.error('Error fetching invoice details:', err));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'Amount') {
      let newAmount = parseFloat(value) || 0;
      if (formData.TotalBalAmt !== 0) {
        const restricted = Math.min(newAmount, formData.TotalBalAmt);
        setFormData(prev => ({ ...prev, Amount: restricted }));
        let remaining = restricted;
        setInvoiceDetails(invoiceDetails.map(inv => {
          const paid = Math.min(inv.PendingAmount, remaining);
          remaining -= paid;
          return { ...inv, Amount: parseFloat(paid), BalanceAmount: parseFloat(Math.max(inv.PendingAmount - paid, 0)) };
        }));
      } else {
        setFormData(prev => ({ ...prev, Amount: newAmount }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handlePaymentTypeChange = (selected) => {
    setSelectedPaymentmethodType(selected);
    setFormData(prev => ({ ...prev, PaymentType: selected.value }));
    setShowBankField(['Cheque', 'RTGS', 'NEFT', 'UPI'].includes(selected.label));
  };

  const handleBankChange = (selected) => { setSelectedBank(selected); };

  const handleCalculation = (e, index) => {
    const value = e.target.value;
    const updated = invoiceDetails.map((inv, idx) => {
      if (idx !== index) return inv;
      const typedAmt = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
      const pending  = parseFloat(inv.PendingAmount) || 0;
      // Paid cannot exceed Pending
      const paidAmt  = Math.min(Math.max(typedAmt, 0), pending);
      const balAmt   = parseFloat((pending - paidAmt).toFixed(2));
      return { ...inv, Amount: paidAmt, BalanceAmount: balAmt };
    });
    // Sum of all Paid values drives the top Amount field
    const total = updated.reduce((sum, inv) => sum + (parseFloat(inv.Amount) || 0), 0);
    setFormData(prev => ({ ...prev, Amount: parseFloat(total.toFixed(2)) }));
    setInvoiceDetails(updated);
  };

  const submitData = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    if (!formData.MasterId) { setErrorMessage('Customer is required!'); setIsLoading(false); return; }
    if (!formData.Amount || formData.Amount === 0) { setErrorMessage('Amount must be greater than 0!'); setIsLoading(false); return; }
    if (selectedPaymentmethodType && selectedPaymentmethodType.label !== 'Cash') {
      if (!formData.ChequeNumber) { setErrorMessage('Reference No is required!'); setIsLoading(false); return; }
      if (!formData.ChequeDate)   { setErrorMessage('Reference Date is required!'); setIsLoading(false); return; }
      if (!formData.chequeDetails){ setErrorMessage('Reference Details are required!'); setIsLoading(false); return; }
      if (!selectedBank)          { setErrorMessage('Bank selection is required!'); setIsLoading(false); return; }
    }
    try {
      await postData('ReceiptApi', {
        PaymentAndReceipt: { ...formData, CreatedBy: userId, BankId: selectedBank?.value || null },
        PaymentAndReceiptSaleCollection: invoiceDetails,
      }, token);
      setSuccessMessage('Receipt Created Successfully!');
      setIsLoading(false);
      history.push('/ReceiptIndex');
    } catch (err) {
      console.error('Error creating receipt:', err);
      setErrorMessage('Failed to create. Please try again.');
      setIsLoading(false);
    }
  };

  const isCashPayment = !selectedPaymentmethodType || selectedPaymentmethodType.label === 'Cash';

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ReceiptIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Create Receipt (Against Sale)</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={submitData} disabled={isLoading}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' }}>
              {isLoading ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : '✓ Save'}
            </button>
            <Link to={{ pathname: '/ReceiptIndex', state: { from: '/ReceiptCreate' } }} onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {errorMessage   && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '7px', fontSize: '11px', color: C.danger }}>{errorMessage}</div>}
      {successMessage && <div style={{ marginBottom: '8px', padding: '6px 12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', fontSize: '11px', color: '#16a34a' }}>{successMessage}</div>}

      {/* ── Entry Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Entry Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={lbl}>Customer <span style={{ color: C.danger }}>*</span></label>
            <Select
              value={selectedCustomer}
              onChange={handleCustomerSelect}
              options={customerOptions}
              placeholder="Select Customer…"
              isSearchable
              menuPortalTarget={document.body}
              styles={selectStyles}
            />
          </div>
          <div>
            <label style={lbl}>Date <span style={{ color: C.danger }}>*</span></label>
            <input type="date" name="BillDate" value={formatDateForInput(formData.BillDate)} onChange={handleChange} style={inp} />
          </div>
          <div>
            <label style={lbl}>Bill Number</label>
            <input type="text" value={`${formData.BillNo || ''}/${formData.FinYear || ''}`} readOnly style={{ ...inp, backgroundColor: '#f0f4f4', cursor: 'default' }} />
          </div>
          <div>
            <label style={lbl}>Amount <span style={{ color: C.danger }}>*</span></label>
            <input
              type="number"
              name="Amount"
              value={formData.Amount}
              onChange={handleChange}
              style={{
                ...inpR,
                backgroundColor: invoiceDetails.length > 0 ? '#f0fdf4' : undefined,
                cursor: invoiceDetails.length > 0 ? 'default' : undefined,
                fontWeight: invoiceDetails.length > 0 ? 700 : undefined,
              }}
              readOnly={invoiceDetails.length > 0}
              title={invoiceDetails.length > 0 ? 'Auto-calculated from Paid amounts below' : ''}
            />
            {invoiceDetails.length > 0 && (
              <span style={{ fontSize: '9px', color: C.muted, marginTop: '2px', display: 'block' }}>
                ∑ sum of paid amounts
              </span>
            )}
          </div>
          <div>
            <label style={lbl}>Remarks</label>
            <input type="text" name="Remarks" value={formData.Remarks || ''} onChange={handleChange} placeholder="Remarks" style={inp} />
          </div>
        </div>
      </div>

      {/* ── Payment Details ── */}
      <div style={card}>
        <div style={cardHead('#f59e0b')}>💳 Payment Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Payment Mode <span style={{ color: C.danger }}>*</span></label>
            <Select value={selectedPaymentmethodType || { value: 'cash', label: 'Cash' }} onChange={handlePaymentTypeChange} options={paymentmethodOptions}
              placeholder="Select mode…" isSearchable menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>Bank</label>
            <Select value={selectedBank} onChange={handleBankChange} options={bankOptions}
              placeholder="Select bank…" isSearchable isDisabled={isCashPayment} menuPortalTarget={document.body} styles={selectStyles} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque No' : 'Reference No'}</label>
            <input type="text" name="ChequeNumber" value={formData.ChequeNumber || ''} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque Date' : 'Reference Date'}</label>
            <input type="date" name="ChequeDate" value={formatDateForInput(formData.ChequeDate)} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
          <div>
            <label style={lbl}>{selectedPaymentmethodType?.value === 3 ? 'Cheque Details' : 'Reference Details'}</label>
            <input type="text" name="chequeDetails" value={formData.chequeDetails || ''} onChange={handleChange}
              disabled={isCashPayment} style={{ ...inp, backgroundColor: isCashPayment ? '#f0f4f4' : '#f8fafa' }} />
          </div>
        </div>
      </div>

      {/* ── Invoice Allocation Table ── */}
      {invoiceDetails.length > 0 && (
        <div style={card}>
          <div style={cardHead('#8b5cf6')}>📄 Invoice Allocation</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafa', borderBottom: `2px solid ${C.cardBorder}` }}>
                  {['#', 'Bill Details', 'Amount', 'Pending', 'Paid', 'Balance'].map((h, i) => (
                    <th key={i} style={{ padding: '7px 8px', fontSize: '10px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: i >= 2 ? 'right' : 'left' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoiceDetails.map((item, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? C.card : '#f7fdfc', borderBottom: `1px solid ${C.cardBorder}` }}>
                    <td style={{ padding: '4px 8px', color: C.muted, width: '40px' }}>{index + 1}</td>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>{item.SaleDescription}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <input type="number" value={item.SaleAmount} readOnly
                        style={{ ...inpR, width: '90px', backgroundColor: '#fef9ec' }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <input type="number" value={item.PendingAmount || 0} readOnly
                        style={{ ...inpR, width: '90px', backgroundColor: '#ecfdf5' }} />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <input
                        type="number"
                        value={item.Amount || 0}
                        onChange={(e) => handleCalculation(e, index)}
                        min={0}
                        max={item.PendingAmount || 0}
                        style={{
                          ...inpR,
                          width: '90px',
                          borderColor: parseFloat(item.Amount) > parseFloat(item.PendingAmount)
                            ? C.danger
                            : parseFloat(item.Amount) > 0 && parseFloat(item.Amount) === parseFloat(item.PendingAmount)
                              ? '#16a34a'
                              : undefined,
                          backgroundColor: parseFloat(item.Amount) > 0 ? '#f0fdf4' : undefined,
                        }}
                      />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <input type="number" value={item.BalanceAmount || 0} readOnly
                        style={{ ...inpR, width: '90px', backgroundColor: '#fef2f2' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReceiptCreate;
